import {otsBaseApi} from "../../ots-api";
import {ServiceType, ServiceTypePatchDTO} from "../../../model/service-type";
import {ServiceTypeFacet, ServiceTypeFacetSaveDTO} from "../../../model/service/service-type-facet";
import {ServiceTypeMode, ServiceTypeModeSaveDTO} from "../../../model/service/service-type-mode";
import {ServiceTypeFacetMode, ServiceTypeFacetModeSaveDTO} from "../../../model/service/service-type-facet-mode";
import {ServiceTypeFreightTypeAssoc} from "../../../model/service-type-freight-type-assoc";

const ALL_TAG_TYPES = [
  "ServiceTypes",
  "ServiceTypeFacets",
  "ServiceTypeModes",
  "ServiceTypeFacetModes",
  "ServiceTypesFreightTypeAssocs",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const serviceTypeApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllServiceTypes: builder.query<ServiceType[], string | undefined>({
      query: (regionId) => ({
        url: `/service-types/all`,
        method: "GET",
        params: regionId ? {regionId: regionId} : undefined
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "ServiceTypes", id} as const)),
            {type: "ServiceTypes", id: "LIST"}
          ];
        } else {
          return [{type: "ServiceTypes", id: "LIST"}];
        }
      }
    }),
    getServiceType: builder.query<ServiceType, string>({
      query: (id) => ({
        url: `/service-types/${id}`,
        method: "GET"
      }),
      keepUnusedDataFor: 300,
      providesTags: (result, error, id) => [{type: "ServiceTypes", id}]
    }),
    createServiceType: builder.mutation<ServiceType, Partial<ServiceType>>({
      query: (serviceType) => ({
        url: '/service-types',
        method: 'POST',
        body: serviceType,
      }),
      invalidatesTags: [{type: 'ServiceTypes', id: 'LIST'}],
    }),
    updateServiceType: builder.mutation<ServiceType, Partial<ServiceType>>({
      query: (serviceType) => ({
        url: '/service-types',
        method: 'PUT',
        body: serviceType,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: 'ServiceTypes', id},
        {type: 'ObjectObjectChangeLogs', id}
      ],
    }),
    deleteServiceType: builder.mutation<void, Partial<ServiceType>>({
      query: (serviceType) => ({
        url: '/service-types',
        method: 'DELETE',
        body: serviceType,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'ServiceTypes', id}],
    }),
    patchUpdateServiceType: builder.mutation<ServiceType, { id: string, patch: ServiceTypePatchDTO }>({
      query: ({id, patch}) => ({
        url: `/service-types/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'ServiceTypes', id}],
    }),
    getAllServiceTypeFacets: builder.query<ServiceTypeFacet[], { serviceTypeId: string, companyId: string }>({
      query: ({serviceTypeId, companyId}) => ({
        url: `/service-types/${serviceTypeId}/facets`,
        method: "GET",
        params: {companyId: companyId}
      }),
      providesTags: (result, error, {serviceTypeId, companyId}) => {
        return [{type: "ServiceTypeFacets", id: serviceTypeId + ":" + companyId}]
      }
    }),
    createServiceTypeFacet: builder.mutation<ServiceTypeFacet, ServiceTypeFacetSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/facets`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacets', id: serviceTypeId + ":" + companyId},
      ],
    }),
    updateServiceTypeFacet: builder.mutation<ServiceTypeFacet, ServiceTypeFacetSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/facets/${dto.facet}`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacets', id: serviceTypeId + ":" + companyId},
      ],
    }),
    deleteServiceTypeFacet: builder.mutation<void, Partial<ServiceTypeFacet>>({
      query: (facet) => ({
        url: '/service-types/facets',
        method: 'DELETE',
        body: facet,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacets', id: serviceTypeId + ":" + companyId},
      ],
    }),
    getAllServiceTypeModes: builder.query<ServiceTypeMode[], { serviceTypeId: string, companyId: string }>({
      query: ({serviceTypeId, companyId}) => ({
        url: `/service-types/${serviceTypeId}/modes`,
        method: "GET",
        params: {companyId: companyId}
      }),
      providesTags: (result, error, {serviceTypeId, companyId}) => {
        return [{type: "ServiceTypeModes", id: serviceTypeId + ":" + companyId}]
      }
    }),
    createServiceTypeMode: builder.mutation<ServiceTypeMode, ServiceTypeModeSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/modes`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    updateServiceTypeMode: builder.mutation<ServiceTypeMode, ServiceTypeModeSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/modes/${dto.modeCode}`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    deleteServiceTypeMode: builder.mutation<void, Partial<ServiceTypeMode>>({
      query: (mode) => ({
        url: '/service-types/modes',
        method: 'DELETE',
        body: mode,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    getAllServiceTypeFacetModes: builder.query<ServiceTypeFacetMode[], { serviceTypeId: string, companyId: string }>({
      query: ({serviceTypeId, companyId}) => ({
        url: `/service-types/${serviceTypeId}/facet-modes`,
        method: "GET",
        params: {companyId: companyId}
      }),
      providesTags: (result, error, {serviceTypeId, companyId}) => {
        return [{type: "ServiceTypeFacetModes", id: serviceTypeId + ":" + companyId}]
      }
    }),
    createServiceTypeFacetMode: builder.mutation<ServiceTypeFacetMode, ServiceTypeFacetModeSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/facet-modes`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacetModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    updateServiceTypeFacetMode: builder.mutation<ServiceTypeFacetMode, ServiceTypeFacetModeSaveDTO>({
      query: (dto) => ({
        url: `/service-types/${dto.serviceTypeId}/facet-modes/${dto.modeCode}`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacetModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    deleteServiceTypeFacetMode: builder.mutation<void, Partial<ServiceTypeFacetMode>>({
      query: (mode) => ({
        url: '/service-types/facet-modes',
        method: 'DELETE',
        body: mode,
      }),
      invalidatesTags: (result, error, {serviceTypeId, companyId}) => [
        {type: 'ServiceTypeFacetModes', id: serviceTypeId + ":" + companyId},
      ],
    }),
    getServiceTypeFreightTypeAssocsByServiceType: builder.query<ServiceTypeFreightTypeAssoc[], {
      serviceTypeId: string,
      regionId?: string
    }>({
      query: ({serviceTypeId, regionId}) => ({
        url: `/service-types/${serviceTypeId}/water-type-assocs`,
        method: "GET",
        params: regionId ? {regionId: regionId} : undefined
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({serviceTypeId}) => ({type: "ServiceTypesFreightTypeAssocs", id: serviceTypeId} as const)),
          ];
        } else {
          return [];
        }
      }
    }),
  })
});

export const {
  useGetAllServiceTypesQuery,
  useGetServiceTypeQuery,
  useCreateServiceTypeMutation,
  useUpdateServiceTypeMutation,
  useDeleteServiceTypeMutation,
  usePatchUpdateServiceTypeMutation,
  useGetAllServiceTypeFacetsQuery,
  useCreateServiceTypeFacetMutation,
  useUpdateServiceTypeFacetMutation,
  useDeleteServiceTypeFacetMutation,
  useGetAllServiceTypeModesQuery,
  useCreateServiceTypeModeMutation,
  useUpdateServiceTypeModeMutation,
  useDeleteServiceTypeModeMutation,
  useGetAllServiceTypeFacetModesQuery,
  useCreateServiceTypeFacetModeMutation,
  useUpdateServiceTypeFacetModeMutation,
  useDeleteServiceTypeFacetModeMutation,
  useGetServiceTypeFreightTypeAssocsByServiceTypeQuery,
} = serviceTypeApi;
